import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCommunityMembers,
  getCommunityMemberById,
  updateCommunityMember,
  deleteCommunityMember,
} from '../services/api';

export const fetchCommunityMembers = createAsyncThunk(
  'community/fetchMembers',
  async (params, { rejectWithValue }) => {
    try {
      const data = await getCommunityMembers(params);
      return Array.isArray(data) ? data : data?.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch community members'
      );
    }
  }
);

export const fetchCommunityMemberById = createAsyncThunk(
  'community/fetchMemberById',
  async (id, { rejectWithValue }) => {
    try {
      const data = await getCommunityMemberById(id);
      return data?.data || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch community member'
      );
    }
  }
);

export const updateCommunityMemberAsync = createAsyncThunk(
  'community/updateMember',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateCommunityMember(id, data);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update community member'
      );
    }
  }
);

export const deleteCommunityMemberAsync = createAsyncThunk(
  'community/deleteMember',
  async (id, { rejectWithValue }) => {
    try {
      await deleteCommunityMember(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to delete community member'
      );
    }
  }
);

const communitySlice = createSlice({
  name: 'community',
  initialState: {
    members: [],
    selectedMember: null,
    status: 'idle',
    error: null,
    actionLoading: false,
    actionError: null,
  },
  reducers: {
    clearCommunityError: (state) => {
      state.error = null;
      state.actionError = null;
    },
    setSelectedMember: (state, action) => {
      state.selectedMember = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch members
      .addCase(fetchCommunityMembers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCommunityMembers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.members = action.payload;
      })
      .addCase(fetchCommunityMembers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Fetch single member
      .addCase(fetchCommunityMemberById.fulfilled, (state, action) => {
        state.selectedMember = action.payload;
      })
      // Update member
      .addCase(updateCommunityMemberAsync.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateCommunityMemberAsync.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        if (updated && updated.id) {
          state.members = state.members.map((m) =>
            m.id === updated.id ? { ...m, ...updated } : m
          );
        }
      })
      .addCase(updateCommunityMemberAsync.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })
      // Delete member
      .addCase(deleteCommunityMemberAsync.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deleteCommunityMemberAsync.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.members = state.members.filter((m) => m.id !== action.payload);
      })
      .addCase(deleteCommunityMemberAsync.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const { clearCommunityError, setSelectedMember } = communitySlice.actions;
export default communitySlice.reducer;
